import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyInsightData } from '../../../../../../hooks/get-company-by-id.hook';
import { CompanyName } from '../experience-item-company-name/experience-item-company-name.component';
import { RoleTitle } from '../experience-item-role-title/experience-item-role-title.component';
import { AlertSnackBar } from '../../../../../Common/CustomSnackBar/AlertSnackBar';
import STRING_KEYS from '../../../../../../language/keys';
import ExperienceTags
  from '../../../../../../modules/application/components/match-card/timeline/experience-container/experience-item-tags/experience-item-tags.component';
import { ExperienceTagType }
  from '../../../../../../modules/application/components/match-card/timeline/experience-container/experience-item-tags/experience-item-tags.enums';
import { ExperienceTag } from '../../../../../../modules/application/components/match-card/timeline/experience-container/experience-item-tags/experience-item-tags';
import {
  CompanyHeaderWrapper,
  CompanyDescription,
  NoDescriptionFallback,
  ExperienceLabel,
  Wrapper,
} from './experience-item-company-header.styled';

type CompanyHeaderProps = {
  isSingleRole: boolean,
  companyData: CompanyInsightData,
  title: string,
  showCompanyInsights: boolean,
  talentSkills: SkillData[],
  keyInsights: string,
  employmentPeriod: string,
  trackCompanyInsightsClick?: (
    matchExperiencePeriod: string,
    matchExperienceCompanyId: string,
    matchExperienceCompanyName: string,
  ) => void,
}

const CompanyHeader = ({
  isSingleRole,
  companyData,
  title,
  showCompanyInsights,
  keyInsights,
  employmentPeriod,
  trackCompanyInsightsClick,
  talentSkills,
}: CompanyHeaderProps): JSX.Element => {
  const [error, setError] = useState<ErrorObject>({
    isError: false,
    message: '',
  });
  const { t: translate } = useTranslation();

  const handleCloseAlert = () => {
    setError({
      isError: false,
      message: '',
    });
  };

  const skillTags = useMemo<ExperienceTag[]>(() => {
    return talentSkills?.map((skill) => ({
      text: skill.skillName,
      type: ExperienceTagType.Skill,
      askedByUser: !!skill.askedByUser,
      tagInfo: skill.askedByUser ? translate(STRING_KEYS.TIMELINE_EXPERIENCE_SKILL_TOOLTIP) : undefined,
    })) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentSkills]);

  const industryTags = useMemo<ExperienceTag[]>(() => {
    return companyData.industries?.map((industry) => ({
      text: industry.value,
      type: ExperienceTagType.Industry,
      askedByUser: industry.askedByUser,
    })) || [];
  }, [companyData.industries]);

  const tags = [...skillTags, ...industryTags];

  return (
    <CompanyHeaderWrapper>
      <AlertSnackBar
        title={ translate(STRING_KEYS.ALERT) }
        isOpen={ error.isError }
        onClose={ handleCloseAlert }
        description={ error.message }
        anchor="top-center"
        shouldAutoDismiss
      />
      {isSingleRole && (
        <RoleTitle
          title={ title }
          isSingleRole={ isSingleRole }
        />
      )}
      <CompanyName
        isSingleRole={ isSingleRole }
        companyData={ companyData }
        showCompanyInsights={ showCompanyInsights }
        trackCompanyInsightsClick={ trackCompanyInsightsClick }
        employmentPeriod={ employmentPeriod }
      />
      {companyData.companyDescription ? (
        <CompanyDescription >
          {companyData.companyDescription}
        </CompanyDescription>
      ) : <NoDescriptionFallback />}

      {keyInsights && <Wrapper><ExperienceLabel>{ keyInsights }</ExperienceLabel></Wrapper>}
      {!!tags.length && (
        <ExperienceTags
          tags={ tags.slice(0, 16) }
        />
      )}

    </CompanyHeaderWrapper>
  );
};

CompanyHeader.defaultProps = {
  trackCompanyInsightsClick: undefined,
};

export default React.memo(CompanyHeader);
